import { centralDbRequest } from './centralDbClient';

export interface EngagementSummary {
  totalActions: number;
  uniqueUsers: number;
  byApp: Record<string, number>;
  byDomain: Record<string, number>;
  byAction: Record<string, number>;
  busiestHour: number | null;
}

export interface DailyActivity {
  date: string;
  count: number;
}

export interface HourlyPattern {
  hour: number;
  count: number;
}

export interface AppBreakdown {
  app: string;
  count: number;
}

export interface DomainBreakdown {
  domain: string;
  count: number;
}

export interface ActiveUser {
  userId: string;
  userEmail: string | null;
  count: number;
}

export interface KnownUser {
  userId: string;
  userEmail: string | null;
}

interface QueryParams {
  from: string;
  to: string;
  userIds?: string[];
  app?: string;
  domain?: string;
}

function buildQuery(params: QueryParams): Record<string, string> {
  const q: Record<string, string> = { from: params.from, to: params.to };
  if (params.userIds?.length) q.userIds = params.userIds.join(',');
  if (params.app) q.app = params.app;
  if (params.domain) q.domain = params.domain;
  return q;
}

async function get<T>(path: string, query: Record<string, string>, token?: string | null): Promise<T> {
  const res = await centralDbRequest({ method: 'GET', path, query, token });
  if (!res.ok) throw new Error(`Engagement API error: ${res.status}`);
  return res.data as T;
}

export const engagementApi = {
  getSummary: (params: QueryParams, token?: string | null) =>
    get<EngagementSummary>('/engagement/summary', buildQuery(params), token),

  getDailyActivity: (params: QueryParams, token?: string | null) =>
    get<DailyActivity[]>('/engagement/daily', buildQuery(params), token),

  getHourlyPattern: (params: Omit<QueryParams, 'app' | 'domain'>, token?: string | null) =>
    get<HourlyPattern[]>('/engagement/hourly', buildQuery(params as QueryParams), token),

  getByApp: (params: Omit<QueryParams, 'app' | 'domain'>, token?: string | null) =>
    get<AppBreakdown[]>('/engagement/by-app', buildQuery(params as QueryParams), token),

  getByDomain: (params: Omit<QueryParams, 'app' | 'domain'>, token?: string | null) =>
    get<DomainBreakdown[]>('/engagement/by-domain', buildQuery(params as QueryParams), token),

  getActiveUsers: (params: { from: string; to: string; limit?: number }, token?: string | null) =>
    get<ActiveUser[]>('/engagement/users', {
      from: params.from,
      to: params.to,
      ...(params.limit ? { limit: String(params.limit) } : {}),
    }, token),

  getKnownUsers: (token?: string | null) =>
    get<KnownUser[]>('/engagement/known-users', {}, token),
};
